/**
 * Cart Cross-Sell
 *
 * Handles adding cross-sell product and applying discount code.
 * Integrates with popup welcome state (localStorage: welcome_variant, welcome_primary_product).
 */

(function () {
  'use strict';

  var SELECTORS = {
    config: '[data-cart-cross-sell-config]',
    banner: '[data-cart-cross-sell]',
    cta: '[data-cross-sell-cta]',
    discountText: '[data-discount-text]',
    price40: '[data-price-40]',
    price25: '[data-price-25]',
    badgeText: '[data-badge-text]',
  };

  var AB_VARIANT_KEY = 'pp_ab_variant';

  /**
   * Get current A/B test variant (matches newsletter-popup logic)
   */
  function getABVariant() {
    var stored = localStorage.getItem(AB_VARIANT_KEY);
    if (stored === 'discount40' || stored === 'discount25') {
      return stored;
    }
    return 'discount40'; // Default to 40% if no variant set
  }

  /**
   * Update UI based on A/B variant
   */
  function updateUIForVariant() {
    var variant = getABVariant();
    var is25 = variant === 'discount25';

    var banners = document.querySelectorAll(SELECTORS.banner);
    banners.forEach(function (banner) {
      var basePercent = banner.dataset.baseDiscountPercent || '40';
      var discountText = banner.querySelector(SELECTORS.discountText);
      var price40 = banner.querySelector(SELECTORS.price40);
      var price25 = banner.querySelector(SELECTORS.price25);
      var badgeText = banner.querySelector(SELECTORS.badgeText);

      if (discountText) {
        discountText.textContent = is25 ? '25%' : basePercent + '%';
      }
      if (price40) {
        price40.style.display = is25 ? 'none' : '';
      }
      if (price25) {
        price25.style.display = is25 ? '' : 'none';
      }
      if (badgeText) {
        badgeText.textContent = is25 ? 'SAVE 25%' : 'SAVE ' + basePercent + '%';
      }
    });
  }

  /**
   * The discount set to hand Shopify, given the cart as it stands and the code this
   * banner carries.
   *
   * Both routes that apply a code REPLACE the cart's whole discount set rather than
   * adding to it: /discount/<code> and /cart/update.js alike. So whatever the cart
   * already holds has to be sent alongside the new code or it is silently dropped.
   * That is what used to happen here — a customer who arrived on FREESHIPNEW and
   * then clicked this banner lost the free shipping and was never told.
   *
   * Returns null when there is nothing to preserve. The single-code /discount/ route
   * is Shopify's own, it is one request rather than two, and with an empty cart
   * discount set it cannot lose anything.
   *
   * Mirrored by tests/cart-cross-sell-codes.test.js. Change one, change the other.
   */
  function codesToSend(cart, code) {
    var held = cart && Array.isArray(cart.discount_codes) ? cart.discount_codes : [];
    var wanted = String(code).toLowerCase();
    var keep = held
      .filter(function (entry) {
        /* applicable false is a code the cart is carrying but not honouring — a
           typo, or one that does not fit this basket. Re-sending it achieves
           nothing and risks Shopify rejecting the whole list. */
        return (
          entry &&
          entry.applicable === true &&
          typeof entry.code === 'string' &&
          entry.code.toLowerCase() !== wanted
        );
      })
      .map(function (entry) {
        return entry.code;
      });

    if (!keep.length) return null;
    /* The comma is the endpoint's own separator. */
    return keep.concat([code]).join(',');
  }

  /**
   * Apply a code without dropping the ones already on the cart.
   *
   * Every failure path falls through to the single-code route, which is exactly what
   * this function did before it existed — so this is never worse than the old
   * behaviour, only sometimes better.
   */
  function applyDiscount(code) {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    var single = function () {
      window.location.href = '/discount/' + encodeURIComponent(code) + '?redirect=/cart';
    };

    fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (response) {
        return response.ok ? response.json() : null;
      })
      .then(function (cart) {
        var discount = codesToSend(cart, code);
        if (!discount) {
          single();
          return null;
        }

        return fetch(root + 'cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ discount: discount }),
        })
          .then(function (response) {
            return response.ok ? response.json() : null;
          })
          .then(function (state) {
            /* No discount_codes in the response means this endpoint is not doing what
               we assume it does. Take the documented route rather than guessing, and
               accept losing the other codes over landing on a cart in an unknown
               state. */
            if (!state || !Array.isArray(state.discount_codes)) {
              single();
              return;
            }
            /* Applied or refused, Shopify has decided and the cart page shows the
               result. Nothing here judges the code. */
            window.location.href = '/cart';
          });
      })
      .catch(single);
  }

  /**
   * Add product to cart (and optionally apply discount code)
   */
  function addProductToCart(variantId, discountCode) {
    var button = document.querySelector(SELECTORS.cta);
    if (button) {
      button.classList.add('loading');
      button.setAttribute('aria-disabled', 'true');
    }

    var addBody = JSON.stringify({
      items: [
        {
          id: parseInt(variantId, 10),
          quantity: 1,
        },
      ],
    });

    fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: addBody,
    })
      .then(function (response) {
        if (!response.ok) {
          throw new Error('Failed to add product to cart');
        }
        return response.json();
      })
      .then(function () {
        if (discountCode) {
          applyDiscount(discountCode);
        } else {
          window.location.href = '/cart';
        }
      })
      .catch(function (error) {
        console.error('Cart cross-sell failed:', error);

        if (button) {
          button.classList.remove('loading');
          button.removeAttribute('aria-disabled');
        }

        // Show error state briefly
        var banner = document.querySelector(SELECTORS.banner);
        if (banner) {
          banner.classList.add('cart-cross-sell--error');
          setTimeout(function () {
            banner.classList.remove('cart-cross-sell--error');
          }, 3000);
        }
      });
  }

  /**
   * Handle CTA click
   */
  function handleCtaClick(event) {
    event.preventDefault();

    var button = event.currentTarget;
    var banner = button.closest(SELECTORS.banner);

    if (!banner) return;

    var variantId = banner.dataset.variantId;
    var variant = getABVariant();
    var upsellCode = variant === 'discount25'
      ? banner.dataset.upsellCode25
      : banner.dataset.upsellCode40;

    if (!variantId) {
      console.error('Cart cross-sell: Missing variant ID');
      return;
    }

    // Track cross-sell click
    if (window.CartTracking) {
      window.CartTracking.trackCrossSellClick({
        variantId: variantId,
        productTitle: banner.dataset.productTitle || '',
        productHandle: banner.dataset.productHandle || '',
        discountCode: upsellCode || '',
        abVariant: variant,
      });
    }

    addProductToCart(variantId, upsellCode);
  }

  /**
   * Attach event listeners to CTA buttons
   */
  function attachListeners() {
    // Update UI for A/B variant whenever listeners are attached (handles dynamic content)
    updateUIForVariant();

    var buttons = document.querySelectorAll(SELECTORS.cta);
    buttons.forEach(function (button) {
      // Remove existing listener to prevent duplicates
      button.removeEventListener('click', handleCtaClick);
      button.addEventListener('click', handleCtaClick);
    });
  }

  /**
   * Initialize the cross-sell functionality
   */
  function init() {
    // Update UI based on A/B variant
    updateUIForVariant();

    // Attach listeners to any existing banners
    attachListeners();

    // Listen for cart updates to reinitialize (for AJAX cart updates)
    if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
        // Small delay to let DOM update
        setTimeout(attachListeners, 100);
      });
    }

    // Observe for dynamic section changes (cart drawer opening, etc.)
    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var mutation = mutations[i];
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          for (var j = 0; j < mutation.addedNodes.length; j++) {
            var node = mutation.addedNodes[j];
            if (node.nodeType === Node.ELEMENT_NODE) {
              if (
                (node.matches && node.matches(SELECTORS.banner)) ||
                (node.querySelector && node.querySelector(SELECTORS.banner))
              ) {
                attachListeners();
                return;
              }
            }
          }
        }
      }
    });

    // Observe the entire document body for changes (cross-sell may appear anywhere)
    observer.observe(document.body, { childList: true, subtree: true });
  }

  // Initialize on DOM ready, with a small delay to ensure sections are rendered
  function safeInit() {
    init();
    // Also try again after a short delay in case sections load late
    setTimeout(attachListeners, 100);
    setTimeout(attachListeners, 500);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', safeInit);
  } else {
    safeInit();
  }
})();
